import { IAs2Partner } from '@core/models/as2-message.model';
import { IIntegrationPartnerGroupUpdateRequest } from '@core/models/integration.model';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import integrationsService from 'services/api/integrations.service';
import { integrationErrorMessage } from '../Integrations/integration-user-messages';

interface Props {
    open: boolean;
    groupCode: string;
    rows: IAs2Partner[];
    onClose: () => void;
    onSaved: () => void;
}

const inputClass =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#bd902e] focus:outline-none focus:ring-2 focus:ring-[#bd902e]/30 disabled:bg-slate-50';

function findRow(rows: IAs2Partner[], group: string, test: boolean): IAs2Partner | undefined {
    const upper = group.toUpperCase();
    return rows.find((p) => {
        const isTest = p.environment === 'TEST' || p.code?.toUpperCase() === `${upper}_TEST`;
        return test ? isTest : !isTest;
    });
}

/**
 * Edits the TEST and PRODUCTION rows of one AS2 partner group side by side.
 * Saves via PUT /admin/integrations/partners/group/{groupCode}; blank fields keep the
 * existing row value. Regenerates partnerships.xml on the backend.
 */
const As2PartnerGroupModal: FC<Props> = ({ open, groupCode, rows, onClose, onSaved }) => {
    const testRow = useMemo(() => findRow(rows, groupCode, true), [rows, groupCode]);
    const prodRow = useMemo(() => findRow(rows, groupCode, false), [rows, groupCode]);

    const [form, setForm] = useState<IIntegrationPartnerGroupUpdateRequest>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            partnerName: (prodRow?.name || testRow?.name || '')
                .replace(/ (Production|Test)$/i, '')
                .trim(),
            as2IdTest: testRow?.as2Id || '',
            as2IdProd: prodRow?.as2Id || '',
            as2UrlTest: testRow?.as2Url || '',
            as2UrlProd: prodRow?.as2Url || '',
            contactEmailTest: testRow?.contactEmail || '',
            contactEmailProd: prodRow?.contactEmail || '',
            openas2PartnerNameTest: testRow?.openas2PartnerName || '',
            openas2PartnerNameProd: prodRow?.openas2PartnerName || '',
            keystoreAliasTest: testRow?.keystoreAlias || '',
            keystoreAliasProd: prodRow?.keystoreAlias || '',
            partnerIpTest: testRow?.partnerIp || '',
            partnerIpProd: prodRow?.partnerIp || '',
            enabledTest: testRow?.enabled,
            enabledProd: prodRow?.enabled,
            autoSendOnShipTest: testRow?.autoSendOnShip,
            autoSendOnShipProd: prodRow?.autoSendOnShip,
        });
    }, [open, testRow, prodRow]);

    if (!open) return null;

    const set = (field: keyof IIntegrationPartnerGroupUpdateRequest, value: unknown) =>
        setForm((f) => ({ ...f, [field]: value }));

    const save = async () => {
        if (!form.as2IdTest?.trim() || !form.as2IdProd?.trim()) {
            toast.error('AS2 ID is required for both test and production', { autoClose: 5000 });
            return;
        }
        setSaving(true);
        try {
            await integrationsService.updatePartnerGroup(groupCode, form);
            toast.success(`Partner group ${groupCode} updated`, { autoClose: 5000 });
            onSaved();
            onClose();
        } catch (err) {
            toast.error(integrationErrorMessage(err, 'Could not update partner group.'), { autoClose: 6000 });
        } finally {
            setSaving(false);
        }
    };

    const field = (
        label: string,
        testField: keyof IIntegrationPartnerGroupUpdateRequest,
        prodField: keyof IIntegrationPartnerGroupUpdateRequest,
        placeholder?: string
    ) => (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
                <label className="mb-1 block text-xs font-medium text-blue-800">{label} (test)</label>
                <input
                    className={inputClass}
                    placeholder={placeholder}
                    value={(form[testField] as string) ?? ''}
                    onChange={(e) => set(testField, e.target.value)}
                    disabled={saving}
                />
            </div>
            <div>
                <label className="mb-1 block text-xs font-medium text-purple-800">{label} (production)</label>
                <input
                    className={inputClass}
                    placeholder={placeholder}
                    value={(form[prodField] as string) ?? ''}
                    onChange={(e) => set(prodField, e.target.value)}
                    disabled={saving}
                />
            </div>
        </div>
    );

    const toggle = (
        label: string,
        testField: keyof IIntegrationPartnerGroupUpdateRequest,
        prodField: keyof IIntegrationPartnerGroupUpdateRequest
    ) => (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-blue-800">
                <input
                    type="checkbox"
                    checked={Boolean(form[testField])}
                    onChange={(e) => set(testField, e.target.checked)}
                    disabled={saving}
                />
                {label} (test)
            </label>
            <label className="flex items-center gap-2 text-sm text-purple-800">
                <input
                    type="checkbox"
                    checked={Boolean(form[prodField])}
                    onChange={(e) => set(prodField, e.target.checked)}
                    disabled={saving}
                />
                {label} (production)
            </label>
        </div>
    );

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">
                        Edit partner group <span className="font-mono text-[#bd902e]">{groupCode}</span>
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                        ✕
                    </button>
                </div>

                <p className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    Set separate test and production identities when your partner uses different AS2 IDs per
                    environment (e.g. <strong>ACME_AS2_TEST</strong> for pre-go-live, <strong>ACME_AS2_PROD</strong>{' '}
                    for production). Saving regenerates partnerships.xml.
                </p>

                <div className="space-y-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-slate-800">Display name</label>
                        <input
                            className={inputClass}
                            value={form.partnerName ?? ''}
                            onChange={(e) => set('partnerName', e.target.value)}
                            disabled={saving}
                            placeholder="e.g. Acme Wholesale"
                        />
                    </div>
                    {field('AS2 ID', 'as2IdTest', 'as2IdProd', 'e.g. ACME_AS2_TEST')}
                    {field('AS2 URL', 'as2UrlTest', 'as2UrlProd', 'https://...')}
                    {field('Contact email', 'contactEmailTest', 'contactEmailProd', 'partner@example.com')}
                    {field('OpenAS2 name', 'openas2PartnerNameTest', 'openas2PartnerNameProd', 'e.g. AcmePharmacy')}
                    {field('Keystore alias', 'keystoreAliasTest', 'keystoreAliasProd', 'e.g. acmepharmacy')}
                    {field('Partner IP', 'partnerIpTest', 'partnerIpProd', 'optional')}
                    {toggle('Enabled', 'enabledTest', 'enabledProd')}
                    {toggle('Auto-send on ship', 'autoSendOnShipTest', 'autoSendOnShipProd')}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="rounded bg-[#bd902e] px-4 py-2 text-sm font-medium text-white hover:bg-[#a87e26] disabled:opacity-60"
                    >
                        {saving ? 'Saving…' : 'Save group'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default As2PartnerGroupModal;
